import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  CheckCircle, Lightning, Sparkle, Plus, Trash, ArrowRight, Crown,
} from '@phosphor-icons/react';
import { getActiveAddons, checkoutAddon, addonCheckoutStatus, deactivateAddon } from '../lib/api';
import { useCompany } from '../context/CompanyContext';

const CATEGORY_ORDER = ['operations', 'ai', 'channels', 'shipping', 'finance', 'comms', 'support', 'branding'];
const CATEGORY_LABELS = {
  operations: 'Operations', ai: 'AI', channels: 'Sales channels',
  shipping: 'Shipping', finance: 'Finance', comms: 'Communications',
  support: 'Support', branding: 'Branding',
};

export default function AddonMarketplace() {
  const { company, refresh } = useCompany();
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(null);
  const [polling, setPolling] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pollingRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await getActiveAddons();
      setCatalog(data.catalog || []);
    } catch (e) {
      toast.error('Failed to load add-on catalogue');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // After Stripe redirect — poll status until paid, then refresh
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('session_id');
    const cancelled = params.get('cancelled');
    if (cancelled) {
      toast.error('Checkout cancelled — no charge made');
      navigate('/settings/billing/addons', { replace: true });
      return;
    }
    if (!sid || pollingRef.current) return;
    pollingRef.current = true;
    setPolling(true);
    const poll = async (attempts = 0) => {
      if (attempts >= 10) {
        setPolling(false);
        toast.error('Still waiting on payment confirmation. Refresh in a minute.');
        navigate('/settings/billing/addons', { replace: true });
        return;
      }
      try {
        const { data } = await addonCheckoutStatus(sid);
        if (data.payment_status === 'paid') {
          toast.success(`Add-on activated: ${data.addon_key.replace(/_/g, ' ')}`);
          await load();
          await refresh?.();
          setPolling(false);
          navigate('/settings/billing/addons', { replace: true });
          return;
        }
        if (data.status === 'expired') {
          toast.error('Checkout session expired');
          setPolling(false);
          navigate('/settings/billing/addons', { replace: true });
          return;
        }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch {
        setTimeout(() => poll(attempts + 1), 2000);
      }
    };
    poll();
  }, [location.search, navigate, load, refresh]);

  const handleActivate = async (key) => {
    setBusy(key);
    try {
      const { data } = await checkoutAddon(key, window.location.origin);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start checkout');
      setBusy(null);
    }
  };

  const handleDeactivate = async (key) => {
    if (!window.confirm(`Deactivate this add-on? You'll lose access immediately and won't be refunded for this billing period.`)) return;
    setBusy(key);
    try {
      await deactivateAddon(key);
      toast.success('Add-on deactivated');
      await load();
      await refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to deactivate');
    } finally {
      setBusy(null);
    }
  };

  // Group catalogue by category
  const grouped = {};
  catalog.forEach((a) => {
    if (!grouped[a.category]) grouped[a.category] = [];
    grouped[a.category].push(a);
  });
  const sortedCats = CATEGORY_ORDER.filter((c) => grouped[c]?.length);

  const activeCount = catalog.filter((a) => a.active).length;
  const monthlyAddonTotal = catalog
    .filter((a) => a.active)
    .reduce((sum, a) => sum + (a.price_monthly_usd || 0), 0);

  return (
    <Layout>
      <div className="space-y-6 p-6" data-testid="addon-marketplace-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Sparkle size={28} weight="fill" className="text-brand" /> Add-on Marketplace
            </h1>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
              Layer features and capacity à la carte · Cancel any time
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/billing')} className="rounded-none" data-testid="addons-back-to-billing">
            <Crown size={14} weight="fill" className="mr-2" /> Plan & Billing
          </Button>
        </div>

        {polling && (
          <div className="border border-amber-500 bg-amber-50 p-3 text-xs uppercase font-bold tracking-widest flex items-center gap-2" data-testid="addons-polling">
            <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
            Confirming payment…
          </div>
        )}

        {/* Active summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="addon-summary-row">
          <SummaryTile label="Current plan" value={company?.plan_name || '—'} accent="brand" />
          <SummaryTile label="Active add-ons" value={`${activeCount}`} accent="emerald" />
          <SummaryTile label="Add-on spend" value={`A$${monthlyAddonTotal.toFixed(0)}/mo`} accent="foreground" />
        </div>

        {/* Catalogue grouped by category */}
        {sortedCats.map((cat) => (
          <section key={cat} data-testid={`addon-cat-${cat}`}>
            <h2 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3 border-b border-border pb-2">
              {CATEGORY_LABELS[cat] || cat}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {grouped[cat].map((a) => (
                <AddonCard
                  key={a.key}
                  addon={a}
                  busy={busy === a.key}
                  onActivate={() => handleActivate(a.key)}
                  onDeactivate={() => handleDeactivate(a.key)}
                />
              ))}
            </div>
          </section>
        ))}

        <p className="text-xs text-muted-foreground text-center font-mono pt-4">
          Secured by Stripe · Cancel any time · No proration on cancellation
        </p>
      </div>
    </Layout>
  );
}

const SummaryTile = ({ label, value, accent }) => {
  const accentClass = {
    brand: 'text-brand',
    emerald: 'text-emerald-600',
    foreground: 'text-foreground',
  }[accent] || 'text-foreground';
  return (
    <div className="border border-border p-4">
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accentClass}`}>{value}</div>
    </div>
  );
};

const AddonCard = ({ addon, busy, onActivate, onDeactivate }) => (
  <div
    data-testid={`addon-card-${addon.key}`}
    className={`border p-5 flex flex-col ${addon.active ? 'border-emerald-500 bg-emerald-50/40' : 'border-border'}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="text-sm font-bold tracking-tight flex-1">{addon.name}</div>
      {addon.active && (
        <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 inline-flex items-center gap-1" data-testid={`addon-active-badge-${addon.key}`}>
          <CheckCircle size={10} weight="fill" /> Active
        </span>
      )}
    </div>
    <div className="mt-2 mb-2">
      <span className="text-2xl font-bold tabular-nums">A${addon.price_monthly_usd}</span>
      <span className="text-[10px] text-muted-foreground font-mono ml-1">/mo</span>
      <span className="text-[9px] text-muted-foreground font-mono ml-2">GST inc.</span>
    </div>
    <p className="text-[11px] text-muted-foreground leading-tight mb-4 flex-1">{addon.tagline}</p>

    {(addon.feature_unlocks || []).length > 0 && (
      <ul className="space-y-0.5 mb-4">
        {addon.feature_unlocks.map((f) => (
          <li key={f} className="flex items-center gap-1.5 text-[10px] text-foreground">
            <CheckCircle size={10} weight="fill" className="text-emerald-600 shrink-0" />
            <span className="font-mono uppercase">{f.replace(/_/g, ' ')}</span>
          </li>
        ))}
      </ul>
    )}

    {addon.active ? (
      <Button
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={onDeactivate}
        data-testid={`addon-deactivate-${addon.key}`}
        className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50"
      >
        <Trash size={12} className="mr-1" /> Deactivate
      </Button>
    ) : (
      <Button
        size="sm"
        disabled={busy}
        onClick={onActivate}
        data-testid={`addon-activate-${addon.key}`}
        className="rounded-none bg-brand hover:bg-brand/90"
      >
        {busy ? 'Redirecting…' : (
          <><Plus size={12} className="mr-1" weight="bold" /> Activate <ArrowRight size={11} className="ml-1" weight="bold" /></>
        )}
      </Button>
    )}
  </div>
);
